import { NgModule } from '@angular/core';
import { NoPreloading, RouterModule, Routes } from '@angular/router';
import { HomePageComponent } from './appmain/home-page/home-page.component';
import { AboutComponent } from './appmain/about/about.component';
import { LoginComponent } from './appmain/login/login.component';
import { AuthCallbackComponent } from './appmain/auth-callback/auth-callback.component';
import { labsModeGuard } from './common/guards/labs-mode.guard';
import { authGuard } from './common/guards/auth.guard';
import { adminGuard } from './common/guards/admin.guard';

const routes: Routes = [
  {
    path: '',
    component: HomePageComponent,
    // No `data.seo` here — the home page uses the site-wide defaults from
    // SeoService, which double as the landing page title/description.
  },
  {
    path: 'about',
    component: AboutComponent,
    data: { seo: {
      title: 'About NW Hub — Neverwinter Companion Site',
      description: 'About NW Hub: a free Neverwinter Online companion site for builds, math, simulation and data reference. Roadmap, what’s new and contact.',
    } },
  },
  { path: 'whats-new', redirectTo: 'about', pathMatch: 'full' },
  { path: 'login', component: LoginComponent },
  { path: 'auth/callback/:provider', component: AuthCallbackComponent },
  {
    path: 'account',
    canActivate: [authGuard],
    loadComponent: () => import('./appmain/account/account.component').then(m => m.AccountComponent),
  },
  {
    path: 'shares',
    canActivate: [authGuard],
    loadComponent: () => import('./appmain/shared-builds/shared-builds.component').then(m => m.SharedBuildsComponent),
  },
  {
    path: 'admin',
    canActivate: [adminGuard],
    loadComponent: () => import('./appmain/admin/admin.component').then(m => m.AdminComponent),
  },
  {
    path: 'search',
    loadComponent: () => import('./search/search.component').then(m => m.SearchComponent),
    data: { seo: {
      title: 'Search — NW Hub',
      description: 'Search NW Hub across gear, mounts, companions, insignias, enchantments, artifacts and classes — one query, all catalogs.',
    } },
  },
  { path: 'classes', loadChildren: () => import('./game-info/routes').then(m => m.routes) },
  { path: 'mechanics', loadChildren: () => import('./mechanics/mechanics.module').then(m => m.MechanicsModule) },
  {
    path: 'fashion',
    loadComponent: () => import('./fashion/fashion.component').then(m => m.FashionComponent),
    data: { seo: {
      title: 'Neverwinter Fashion — NW Hub',
      description: 'Browse Neverwinter fashion items and transmute references with previews, sources and rarity.',
    } },
  },
  {
    path: 'combatlogs',
    loadComponent: () => import('./combatlogs/combatlogs.component').then(m => m.CombatlogsComponent),
    data: { seo: {
      title: 'Combat Log Library — NW Hub',
      description: 'Browse shared Neverwinter combat logs and encounter parses. Compare DPS, rotations and proc uptime across builds.',
    } },
  },
  { path: 'logparser', loadChildren: () => import('./logparser/logparser.module').then(m => m.LogparserModule) },
  {
    path: 'discords',
    loadComponent: () => import('./discords/discords.component').then(m => m.DiscordsComponent),
    data: { seo: {
      title: 'Neverwinter Discord Servers — NW Hub',
      description: 'Curated directory of active Neverwinter Discord communities by class, role, language and region.',
    } },
  },
  {
    path: 'guilds',
    loadComponent: () => import('./guilds/guilds.component').then(m => m.GuildsComponent),
    data: { seo: {
      title: 'Neverwinter Guilds — NW Hub',
      description: 'Find active Neverwinter guilds and alliances. Filter by region, language, content focus and stronghold level.',
    } },
  },
  {
    path: 'guilds/admin',
    canActivate: [adminGuard],
    loadComponent: () => import('./guilds/guilds-admin.component').then(m => m.GuildsAdminComponent),
  },
  {
    path: 'news',
    loadComponent: () => import('./news/news.component').then(m => m.NewsComponent),
    data: { seo: {
      title: 'Neverwinter News — NW Hub',
      description: 'Latest Neverwinter Online news, patch notes, events and announcements pulled from the official feed.',
    } },
  },
  {
    path: 'discord-summaries',
    canActivate: [labsModeGuard],
    loadComponent: () => import('./discords/discord-summaries/discord-summaries.component').then(m => m.DiscordSummariesComponent),
  },
  { path: 'rainer', loadChildren: () => import('./rainer/rainer.module').then(m => m.RainerModule) },
  { path: 'sim', canActivate: [labsModeGuard], loadChildren: () => import('./sim/sim.module').then(m => m.SimModule) },
  { path: 'characters', loadChildren: () => import('./character-profile/character-profile.module').then(m => m.CharacterProfileModule) },
  { path: 'profiles', redirectTo: 'characters', pathMatch: 'prefix' },
  { path: 'guild-tools', loadChildren: () => import('./guild-tools/guild-tools.module').then(m => m.GuildToolsModule) },
  { path: 'professions', loadChildren: () => import('./professions/professions.module').then(m => m.ProfessionsModule) },
  {
    path: 'refining',
    loadComponent: () => import('./refining/refining.component').then(m => m.RefiningComponent),
    data: { seo: {
      title: 'Refining Costs — NW Hub',
      description: 'Refining costs and RP requirements to upgrade Neverwinter enchantments, runestones and artifacts to every rank.',
    } },
  },
  {
    path: 'upgrade-cost',
    loadComponent: () => import('./refining-cost/refining-cost.component').then(m => m.RefiningCostComponent),
    data: { seo: {
      title: 'Upgrade Cost Calculator — NW Hub',
      description: 'Plan the AD cost of upgrading Neverwinter enchantments (Uncommon → Celestial) and artifacts (Uncommon → Mythic) through the quality tiers with mote, ward and component math.',
    } },
  },
  { path: 'mounts', loadChildren: () => import('./mounts/mounts.module').then(m => m.MountsModule) },
  { path: 'companions', loadChildren: () => import('./companions/companions.module').then(m => m.CompanionsModule) },
  { path: 'gear', loadChildren: () => import('./gear/gear.module').then(m => m.GearModule) },
  {
    path: 'consumables',
    loadComponent: () => import('./consumables/consumables.component').then(m => m.ConsumablesComponent),
    data: { seo: {
      title: 'Consumables — NW Hub',
      description: 'Neverwinter consumable reference: potions, elixirs, scrolls, food and event food with buff slot, magnitude and duration.',
    } },
  },
  {
    path: 'packs',
    loadComponent: () => import('./packs/packs.component').then(m => m.PacksComponent),
    data: { seo: {
      title: 'Lockboxes & Packs — NW Hub',
      description: 'Neverwinter lockbox and pack contents: mounts, companions, fashion and rewards with rarities and drop tables.',
    } },
  },
  { path: 'enchantments', loadChildren: () => import('./enchantments/enchantments.module').then(m => m.EnchantmentsModule) },
  { path: 'artifacts', loadChildren: () => import('./artifacts/artifacts.module').then(m => m.ArtifactsModule) },
  { path: 'content-helpers', canActivate: [labsModeGuard], loadChildren: () => import('./content-helpers/routes').then(m => m.routes) },
  { path: 'other-info', loadChildren: () => import('./other-info/routes').then(m => m.routes) },
  { path: 'old-janne', loadChildren: () => import('./old-janne/old-janne.module').then(m => m.OldJanneModule) },
  {
    path: '**',
    loadComponent: () => import('./appmain/not-found/not-found.component').then(m => m.NotFoundComponent),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: NoPreloading })],
  exports: [RouterModule]
})
export class AppRoutingModule { }

